import React from "react";
import { WhatsAppConversation } from "../../types/api";
import { User } from "../../types/api";

interface ConversationHeaderProps {
  conversation: WhatsAppConversation;
  showBackButton: boolean;
  onBack: () => void;
  onNavigateToPatient: (patientId: number) => void;
  users?: User[];
  onAssign?: (userId: number | undefined, userName: string | undefined) => void;
  onStatusChange?: (status: 'open' | 'solved' | 'pending') => void;
}

const STATUS_OPTIONS: { value: 'open' | 'solved' | 'pending'; label: string }[] = [
  { value: 'open', label: 'פתוח' },
  { value: 'pending', label: 'ממתין' },
  { value: 'solved', label: 'סגור' },
];

export const ConversationHeader: React.FC<ConversationHeaderProps> = ({
  conversation,
  showBackButton,
  onBack,
  onNavigateToPatient,
  users,
  onAssign,
  onStatusChange,
}) => {
  const displayName = conversation.patient_name || "";
  const displayHmId = conversation.patient_hm_id || "";
  const patientId = conversation.patient_id;

  const handleAssignChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!onAssign) return;
    const val = e.target.value;
    if (val === "") {
      onAssign(undefined, undefined);
    } else {
      const user = users?.find((u) => String(u.id) === val);
      onAssign(user?.id, user?.name);
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!onStatusChange) return;
    onStatusChange(e.target.value as 'open' | 'solved' | 'pending');
  };

  const selectStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: 6,
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
    padding: "4px 8px",
    outline: "none",
  };

  return (
    <div
      style={{
        backgroundColor: "#3A7380",
        color: "#fff",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        position: "relative",
        flexWrap: "wrap",
      }}
    >
      {/* Back button */}
      {showBackButton && (
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            fontSize: 20,
            padding: "0 4px",
            lineHeight: 1,
          }}
        >
          ←
        </button>
      )}

      {/* WhatsApp icon */}
      <div style={{ fontSize: 24, flexShrink: 0 }}>💬</div>

      {/* Patient info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 15, direction: "rtl" }}>
            {displayName || "שיחה לא מזוהה"}
          </span>
        </div>
        <div style={{ fontSize: 12, opacity: 0.85, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ direction: "ltr", unicodeBidi: "plaintext" }}>{conversation.phone_number}</span>
          {displayHmId && <span>· {displayHmId}</span>}
        </div>
      </div>

      {/* Status dropdown */}
      {onStatusChange && (
        <select
          value={conversation.status || "open"}
          onChange={handleStatusChange}
          style={selectStyle}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} style={{ color: "#000" }}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {/* Assign dropdown */}
      {onAssign && users && (
        <select
          value={conversation.assigned_user_id !== undefined ? String(conversation.assigned_user_id) : ""}
          onChange={handleAssignChange}
          style={selectStyle}
        >
          <option value="" style={{ color: "#000" }}>
            {conversation.assigned_user_name || "הקצה נציג"}
          </option>
          {users.map((u) => (
            <option key={u.id} value={String(u.id)} style={{ color: "#000" }}>
              {u.name}
            </option>
          ))}
        </select>
      )}

      {/* View patient link */}
      {patientId && (
        <button
          onClick={() => onNavigateToPatient(patientId)}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: 6,
            color: "#fff",
            cursor: "pointer",
            fontSize: 12,
            padding: "4px 10px",
            whiteSpace: "nowrap",
          }}
        >
          צפה במטופל
        </button>
      )}
    </div>
  );
};
